import { useFileInfo, useFileSignedUrl } from "@/api/services/global/file";
import { IconChevronLeft, IconChevronRight } from "@/assets/icons";
import { Avatar } from "@/components/elements/Avatar";
import { Button } from "@/components/elements/Button";
import { DATE_TIME_FORMAT_WITH_SECONDS } from "@/constants/datetime";
import { MIME_TYPES } from "@/constants/file";
import { EMPTY_STR } from "@/constants/string";
import { TTalentUploadFile } from "@/features/talent/types/talentForm";
import { background, divider, text } from "@/theme/colors";
import { textEllipsis } from "@/theme/minxin";
import { TExtendFile } from "@/types/file";
import { dayjs } from "@/utils/dayjs";
import { showError } from "@/utils/error";
import { downloadFileFromPath } from "@/utils/export";
import { convertByteToKb } from "@/utils/file";
import { Box, Modal, Stack, styled, Typography, Button as ButtonMui, Divider, Grid } from "@mui/material";
import { toNumber } from "lodash";
import { FC, useState, useEffect } from "react";

type TFileWithPath = { filePath: string; name?: string };
type TFileList = TExtendFile[] | TTalentUploadFile[] | TFileWithPath[];

type TTalentImagePreviewModalProps = {
  currentFileIndex: number;
  filePath?: string;
  file?: File;
  imageList?: TFileList;
  open: boolean;
  onClose: () => void;
  onNext: () => void;
  onPrev: () => void;
};

export const TalentImagePreviewModal: FC<TTalentImagePreviewModalProps> = ({ currentFileIndex, filePath, imageList, open, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(currentFileIndex);
  const [direction, setDirection] = useState<"next" | "prev" | null>(null);
  const [canGoNext, setCanGoNext] = useState(true);
  const [canGoPrev, setCanGoPrev] = useState(true);

  const currentFile = imageList?.[currentIndex] as TFileWithPath | File | undefined;
  const currentFilePath =
    currentFile && !(currentFile instanceof File) && "filePath" in currentFile ? (currentFile as TFileWithPath).filePath : filePath;
  const { data: currentFileInfo } = useFileInfo(currentFilePath);
  const { data: currentFileSignedUrl } = useFileSignedUrl(currentFilePath);

  // Helper function to check if a file is viewable (PDF or Image)
  const isViewableFile = (fileInfo: { data?: { mimeType?: string } } | undefined) => {
    if (!fileInfo?.data?.mimeType) return false;
    return MIME_TYPES.IMAGE.includes(fileInfo.data.mimeType) || fileInfo.data.mimeType === "application/pdf";
  };

  const handleNext = () => {
    if (!imageList?.length) return;

    const nextIndex = currentIndex + 1;

    if (nextIndex >= imageList.length) {
      return;
    }

    setDirection("next");
    setCurrentIndex(nextIndex);
  };

  const handlePrev = () => {
    if (!imageList?.length) return;

    const prevIndex = currentIndex - 1;

    if (prevIndex < 0) {
      return;
    }

    setDirection("prev");
    setCurrentIndex(prevIndex);
  };

  // Update navigation availability when index changes
  useEffect(() => {
    if (currentIndex < (imageList?.length || 0) - 1) {
      setCanGoNext(true);
    } else {
      setCanGoNext(false);
    }

    if (currentIndex > 0) {
      setCanGoPrev(true);
    } else {
      setCanGoPrev(false);
    }
  }, [currentIndex, imageList?.length]);

  // Auto-skip non-viewable files when current file info loads
  useEffect(() => {
    if (currentFileInfo && !isViewableFile(currentFileInfo)) {
      if (direction === "next") {
        const nextIndex = currentIndex + 1;
        if (nextIndex < (imageList?.length || 0)) {
          setCurrentIndex(nextIndex);
          return;
        } else {
          setCanGoNext(false);
          const prevIndex = currentIndex - 1;
          if (prevIndex >= 0) {
            setDirection("prev");
            setCurrentIndex(prevIndex);
            return;
          }
        }
      } else if (direction === "prev") {
        const prevIndex = currentIndex - 1;
        if (prevIndex >= 0) {
          setCurrentIndex(prevIndex);
          return;
        } else {
          setCanGoPrev(false);
          const nextIndex = currentIndex + 1;
          if (nextIndex < (imageList?.length || 0)) {
            setDirection("next");
            setCurrentIndex(nextIndex);
            return;
          }
        }
      } else {
        // Initial load or no direction, try next first
        const nextIndex = currentIndex + 1;
        if (nextIndex < (imageList?.length || 0)) {
          setCurrentIndex(nextIndex);
          return;
        }

        // No next available, try prev
        const prevIndex = currentIndex - 1;
        if (prevIndex >= 0) {
          setCurrentIndex(prevIndex);
          return;
        }
      }

      onClose();
    } else {
      setDirection(null);
    }
  }, [currentFileInfo, currentIndex, imageList?.length, onClose, direction]);

  const handleDownloadFile = async () => {
    try {
      if (currentFilePath) {
        await downloadFileFromPath(currentFilePath);
      }
    } catch (error) {
      showError(error);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalContainer>
        <Stack>
          <Stack gap={2}>
            <FileName variant="sub16Bold" color={text.secondary}>
              {(currentFile instanceof File ? currentFile.name : (currentFile as TFileWithPath)?.name) ?? currentFileInfo?.data?.name}
            </FileName>
            {currentFileInfo && MIME_TYPES.IMAGE.includes(currentFileInfo.data.mimeType) ? (
              <Avatar filePath={currentFilePath} width={800} height={500} />
            ) : (
              <iframe
                src={currentFileSignedUrl?.data.signedUrl}
                style={{ marginBottom: "20px", height: 500, width: 800, border: "none" }}
                title="PDF Page"
                allowFullScreen
              />
            )}
          </Stack>
          <Stack pt={2} gap={0.5}>
            <Grid container spacing={3}>
              <Grid item xs={6}>
                <Typography variant="body14" color={text.secondary}>
                  更新日時
                </Typography>
                <Divider />
                <Typography variant="body14">
                  {currentFileInfo?.data.updatedAt
                    ? dayjs(currentFileInfo?.data.updatedAt).format(DATE_TIME_FORMAT_WITH_SECONDS)
                    : EMPTY_STR.DATE_TIME}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body14" color={text.secondary}>
                  ファイル容量
                </Typography>
                <Divider />
                <Typography variant="body14">{convertByteToKb(currentFileInfo?.data?.size ?? 0)}kb</Typography>
              </Grid>
            </Grid>
          </Stack>
          <Stack py={2} flexDirection="row" gap={2} justifyContent="space-between">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setCurrentIndex(0);
                onClose();
              }}
            >
              キャンセル
            </Button>
            <Button variant="primary" size="sm" onClick={handleDownloadFile}>
              ダウンロード
            </Button>
          </Stack>
          {toNumber(imageList?.length) > 1 && (
            <Stack pt={2} flexDirection="row" gap={2} justifyContent="center" alignItems="center">
              <BoxIcon
                onClick={handlePrev}
                disabled={!canGoPrev}
                style={{ opacity: canGoPrev ? 1 : 0.4, cursor: canGoPrev ? "pointer" : "not-allowed" }}
              >
                <IconChevronLeft width={20} height={20} color={text.primary} />
              </BoxIcon>
              {/* <Typography variant="body14">{`${currentIndex + 1} / ${imageList?.length}`}</Typography> */}
              <BoxIcon
                onClick={handleNext}
                disabled={!canGoNext}
                style={{ opacity: canGoNext ? 1 : 0.4, cursor: canGoNext ? "pointer" : "not-allowed" }}
              >
                <IconChevronRight width={20} height={20} color={text.primary} />
              </BoxIcon>
            </Stack>
          )}
        </Stack>
      </ModalContainer>
    </Modal>
  );
};

const ModalContainer = styled(Box)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  min-width: 348px;
  background-color: ${background.white};
  box-shadow: 0px 8px 24px 0px #00000026;
  border-radius: 8px;
  padding: 24px;
  max-height: 90vh;
`;

const FileName = styled(Typography)`
  max-width: 300px;
  ${textEllipsis};
  flex: 1;
`;

const BoxIcon = styled(ButtonMui)`
  padding: 0;
  min-width: 24px;
  width: 24px;
  height: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
  border: 1px solid ${divider.middle};
  &:hover {
    cursor: pointer;
    opacity: 0.8;
  }
  &:disabled {
    cursor: not-allowed;
    opacity: 0.4;
  }
`;
